import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { MessageNotification } from "../../../cores/notifications/interfaces/api/get-notifications";
import { SocialProviderNameMapper } from "../../../cores/socialPost/config";
import { SocialProviderIcon } from "../socialPost/selector/SocialProviderIcon";
import { getFirstLetters } from "../../utils/stringUtils";
import "./MessageNotificationItem.scss";

interface Props {
  notification: MessageNotification;
  variant?: "dropdown" | "page";
  isDarkMode?: boolean;
}

function channelLabelKey(type: string): string {
  const t = (type || "").toLowerCase();
  if (t === "whatsapp") return "app.notifications.channel.whatsapp";
  if (t === "facebook") return "app.notifications.channel.facebook";
  if (t === "instagram") return "app.notifications.channel.instagram";
  if (t === "web") return "app.notifications.channel.web";
  return "app.notifications.channel.chat";
}

function ChannelVisual({
  channel,
  className,
}: {
  channel: string;
  className?: string;
}) {
  const ch = (channel || "").toLowerCase();
  if (ch === "whatsapp") {
    return (
      <FontAwesomeIcon
        icon={["fab", "whatsapp"]}
        className={cn("message-notification-page__channel-icon whatsapp", className)}
      />
    );
  }
  if (ch === "facebook") {
    return (
      <FontAwesomeIcon
        icon={["fab", "facebook"]}
        className={cn("message-notification-page__channel-icon facebook", className)}
      />
    );
  }
  if (ch === "instagram") {
    return (
      <FontAwesomeIcon
        icon={["fab", "instagram"]}
        className={cn("message-notification-page__channel-icon instagram", className)}
      />
    );
  }
  if (ch === "web") {
    return (
      <FontAwesomeIcon
        icon={["fas", "globe"]}
        className={cn("message-notification-page__channel-icon web", className)}
      />
    );
  }
  return (
    <SocialProviderIcon
      className={className}
      isSelected
      showErrors={false}
      socialProvider={{ id: channel }}
    />
  );
}

export default function MessageNotificationItem({
  notification,
  variant = "dropdown",
  isDarkMode = false,
}: Props) {
  const intl = useIntl();

  const getLabelSnippet = ({
    message,
    messageType,
  }: MessageNotification) => {
    switch (messageType) {
      case "audio":
        return intl.formatMessage({ id: "app.chat.sentAudio" });
      case "video":
        return intl.formatMessage({ id: "app.chat.sentVideo" });
      case "image":
        return intl.formatMessage({ id: "app.chat.sentImage" });
      case "document":
        return intl.formatMessage({ id: "app.chat.sentAttachment" });
      default:
        return message;
    }
  };

  const snippet = getLabelSnippet(notification);
  const networkName =
    SocialProviderNameMapper[notification.type] ||
    SocialProviderNameMapper[notification.type?.toLowerCase?.()] ||
    notification.type;

  const messageTypeLabel = useMemo(() => {
    switch (notification.messageType) {
      case "audio":
        return intl.formatMessage({ id: "app.notifications.msgType.audio" });
      case "video":
        return intl.formatMessage({ id: "app.notifications.msgType.video" });
      case "image":
        return intl.formatMessage({ id: "app.notifications.msgType.image" });
      case "document":
        return intl.formatMessage({ id: "app.notifications.msgType.document" });
      default:
        return intl.formatMessage({ id: "app.notifications.msgType.text" });
    }
  }, [notification.messageType, intl]);

  if (variant === "page") {
    return (
      <div
        className={cn("message-notification-page", {
          "message-notification-page--dark": isDarkMode,
        })}
      >
        <div className="message-notification-page__avatar" aria-hidden>
          <ChannelVisual channel={notification.type} />
        </div>
        <div className="message-notification-page__body">
          <p className="message-notification-page__title">
            {intl.formatMessage({ id: "app.notifications.messageIncoming" })}
          </p>
          <p className="message-notification-page__sender">
            {notification.from.name}
          </p>
          {notification.from.email ? (
            <p className="message-notification-page__email">
              {notification.from.email}
            </p>
          ) : null}
          <p className="message-notification-page__preview">{snippet}</p>
          <div className="message-notification-page__chips">
            <span className="message-notification-page__chip">{networkName}</span>
            <span className="message-notification-page__chip">
              {intl.formatMessage({ id: channelLabelKey(notification.type) })}
            </span>
            <span className="message-notification-page__chip">
              {messageTypeLabel}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="message-notification-dropdown">
      <SocialProviderIcon
        className="mr-3"
        isSelected
        showErrors={false}
        socialProvider={{
          id: notification.type,
        }}
      />
      <div className="d-flex flex-column overflow-hidden">
        <span>
          {intl.formatMessage(
            { id: "app.notifications.newMessageFrom" },
            { network: networkName }
          )}
        </span>
        <span className="text-truncate text-muted">
          {notification.from.name}: {snippet}
        </span>
      </div>
    </div>
  );
}
